<template>
  <div>
    <div v-if="!editing" class="d-flex align-items-center gap-2">
      <span class="fs-7 fw-semibold" data-testid="mandate-name">{{
        name
      }}</span>
      <EditButton @click="startEditing" />
    </div>
    <template v-else>
      <input
        ref="input"
        :value="name"
        type="text"
        class="form-control"
        :class="{ 'is-invalid': name.trim() === '' }"
        :maxlength="MANDATE_NAME_MAX"
        :aria-label="$t('forwards.mandates.editor.name')"
        @input="emit('edit', ($event.target as HTMLInputElement).value)"
      />
      <div class="invalid-feedback">
        {{ $t("forwards.mandates.editor.name_required") }}
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import EditButton from "@/components/common/EditButton.vue";
import { MANDATE_NAME_MAX } from "@/forwards2/mandates/mandateFormRules";
import { nextTick, ref, useTemplateRef } from "vue";

/**
 * A mandate's name as text, with an edit button that turns it into an input for the rest of the
 * session; an emptied name is marked there, and the save holds back on it. Each keystroke is
 * emitted, so the parent can stop generating the name from that moment.
 *
 * The wizard remounts the field on every step change, so `edited` (the user has typed a name)
 * brings the input back rather than hiding an emptied name behind the text and its button.
 */
const props = withDefaults(
  defineProps<{
    name: string;
    edited?: boolean;
  }>(),
  { edited: false }
);

const emit = defineEmits<{
  edit: [name: string];
}>();

const editing = ref(props.edited);
const input = useTemplateRef<HTMLInputElement>("input");

const startEditing = async () => {
  editing.value = true;
  await nextTick();
  input.value?.focus();
};
</script>
